"use client";

import Script from "next/script";
import { isSentinelValue } from "@/lib/env";

/**
 * Google Tag Manager container loader.
 *
 * Injects the official GTM snippet (shared `dataLayer`) so a Custom Event
 * trigger can fire a Google Ads Conversion Tracking tag on
 * `{ event: "signup_complete" }`. GA4 events stay on the existing gtag
 * facade — do not add a GA4 tag in this container or page views / `sign_up`
 * will duplicate.
 *
 * Must be a Client Component so that `process.env.NEXT_PUBLIC_*` is inlined
 * into the client bundle (which entrypoint.sh replaces at container start),
 * rather than evaluated in a statically pre-rendered Server Component at
 * build time when the env vars still hold sentinel placeholder values.
 *
 * Only renders when NEXT_PUBLIC_GTM_ID looks like `GTM-…` and we are not in
 * a test environment, so local/CI Playwright runs never load the real script.
 */

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;

const resolveGtmId = (): string | null => {
  const raw = process.env.NEXT_PUBLIC_GTM_ID;
  if (!raw || isSentinelValue(raw) || process.env.NODE_ENV === "test") {
    return null;
  }
  const id = raw.trim();
  return GTM_ID_PATTERN.test(id) ? id : null;
};

export const GtmTrackingScript = () => {
  const gtmId = resolveGtmId();
  if (!gtmId) {
    return null;
  }

  return (
    <Script
      id="gtm-loader"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.dataLayer = window.dataLayer || [];
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `,
      }}
    />
  );
};

/** Official GTM noscript iframe — must live in `<body>`, not `<head>`. */
export const GtmNoscript = () => {
  const gtmId = resolveGtmId();
  if (!gtmId) {
    return null;
  }

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="Google Tag Manager"
      />
    </noscript>
  );
};
